import React, { Component, ReactNode } from "react";
import { ColumnType, TableRowSelection } from "antd/lib/table/interface";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import Button from "antd/lib/button";
import Select from "antd/lib/select";
import Row from "antd/lib/row";
import Col from "antd/lib/col";
import Input from "antd/lib/input";
import Checkbox from "antd/lib/checkbox";
import ConfigProvider from "antd/lib/config-provider";
import ZModal from "@atoms/ZModal";

import NoProductFoundIcon from "@/Assets/ProductsPage/NoProductFoundIcon.svg";
import SearchSomethingProductsIcon from "@/Assets/ProductsPage/StartSearching.svg";

import {
	Product,
	ProductSource,
	ProductMetaInfo,
	ProductType,
	UOM,
	SortOptions,
	ProductSearchRequest,
	AmazonRefinementsFilters,
	AmazonProductAppliedFilters,
	AmazonFilterProps,
	AmazonCatalogFilterKeys,
	ItemSearchCursor
} from "@/Contracts/Products";
import {
	UnifiedSearchRequest,
	UnifiedPricingRequest,
	UnifiedSearchItem,
	UnifiedSearchFilters
} from "@/Contracts/Products/UnifiedSearch";
import {
	prepareIdentifiersForPricing,
	flattenPricingResponseToProducts,
	convertAggregationsToMetaInfo,
	buildAggregationFilters
} from "@/Contracts/Products/UnifiedSearchUtils";

import {
	searchUnifiedProducts,
	searchUnifiedProductsPricing,
	getUnifiedProductsAggregations,
	searchProducts
} from "@/Services/Products";
import ProductCatalogResultRow from "../../../../ProductsPage/Drawers/ProductCatalogResultRow/ProductCatalogResultRow";
import UnifiedItemFiltersMenu from "../DocumentProductDrawersComponents/UnifiedItemFiltersMenu/UnifiedItemFiltersMenu";
import AmazonItemFiltersMenu from "../DocumentProductDrawersComponents/ItemFilterMenu/AmazonItemFiltersMenu";
import { SearchOutlined } from "@ant-design/icons";
import AddProductDrawerV2 from "@pages/Drawers/Products/Product";
import { EProductDrawerState } from "@pages/Drawers/Products/Product/interface";
import ZTypography from "@atoms/ZTypography";
import ZMessage from "@molecules/ZMessage";
import { cache } from "@/Contracts";
import {
	AllRolePermissions,
	PricingAddOnEnum,
	PricingPlanNames,
	UserRole
} from "@/Contracts/Users";
import ZFeatureAccess from "@atoms/ZFeatureAccess";
import {
	constructProductIdToPricingMapping,
	getPreferredPricingForProduct,
	selectPricingBasedOnSort
} from "@/V2Utils";
import ZTable from "@organisms/ZTable";
import { VendorSubscriptionStatus } from "@/Contracts/Marketplace/Vendor";
import ZUpdateMandatoryFieldsForQuoteBlockDrawer from "@templates/ZDocuments/ZUpdateMandatoryFieldsForQuoteBlockDrawer";
import ZUpdateProductCatalogResultColumn from "@templates/ZProductDetails/ZUpdateProductCatalogResultColumn";

import "./UnifiedSearchDocumentProductCatalogDrawer.less";
import { useFeatureFlag } from "@/Hooks/useFeature";
import { useSelector, useDispatch } from "react-redux";
import { GlobalState } from "@/Reducers/interface";
import { ProductsActionCreators } from "@/Actions/Products";
import UsersActionCreators from "@/Actions/User";
import { proposalBuilderStore } from "@/ProposalBuilder/store";
import { ZIcon } from "@/V2Components/UI/atoms";
import { EIllustrations } from "@/V2Components/UI/atoms/ZIcon/interface";
import { IntegrateAmazonForm } from "../DocumentProductDrawersComponents/AmazonIntegrateForm";
import ZButton from "@atoms/ZButton";
import { useFeatureAccess } from "@/Hooks/useFeatureAccess";
import usePermissions from "@/Hooks/usePermissions";
import { Modules, ModulePermission } from "@/Contracts/Permission";

const { ZText } = ZTypography;

export interface Props {
	readonly onSubmit: (
		selectedProducts: Product[],
		productVsPricing: Record<string, string | null>,
		addingToBundle?: boolean
	) => void;
	readonly onClose?: ((e: any) => void) | undefined;
	readonly isLoading: boolean;
	readonly excludeBundles?: boolean;
	readonly itemType?: string;
	readonly isFromProductsPage: boolean;
	readonly configureDrawer?: ReactNode;
	readonly unfilledSelectedQuoteProductIds?: string[];
	readonly removeQuoteProducts?: (quoteProductIds: string[]) => void;
	readonly resetQuoteProducts?: () => void;
	readonly configureQuoteProduct?: (productId: string) => void;
	readonly source?: ProductSource | null;
	readonly createProductPermission?: AllRolePermissions[];
	readonly productVsPricing?: Record<string, string | null>;
	readonly marketplaceFlag?: boolean;
	readonly showPartnerAlignFeaturesFlag?: boolean;
	readonly hasPermissionForAmazonDistributor?: boolean;
	readonly isInternalUser?: boolean;
	readonly isAmazonDistributorIntegrated?: boolean;
	readonly openImportDrawer?: () => void;
	readonly vendorProgramDetails?: any[];
	readonly updateSelectedProduct?: (selectedProduct: Product) => void;
	readonly amazonFilterState?: AmazonFilterProps;
	readonly amazonAppliedFilters?: AmazonProductAppliedFilters;
	readonly updateAmazonAppliedFilters?: (
		amazonAppliedFilters: AmazonProductAppliedFilters
	) => void;
	readonly isDickerDataIntegrated?: boolean;
}

export interface State {
	readonly loading: boolean;
	readonly pricingLoading: boolean;
	readonly searchQuery: string;
	readonly dataSource: Product[];
	readonly metaInfo: ProductMetaInfo;
	readonly selectedProductList: Product[];
	readonly currentPageNo: number;
	readonly hasMore: boolean;
	readonly productVsPricing: Record<string, string | null>;
	readonly filters: UnifiedSearchFilters;
	readonly showMpnSearch: boolean;
	readonly mpnQuery: string;
	readonly createProductButtonVisible: boolean;
	readonly lastSearchItems: UnifiedSearchItem[];
	readonly hasSearched: boolean;
	readonly sortBy: SortOptions | undefined;
	readonly currentItemSource: ProductSource;
	readonly amazonMetaInfo: ProductMetaInfo;
	readonly amazonCursor: ItemSearchCursor | null;
	readonly amazonFilterState: AmazonFilterProps;
	readonly amazonFilterMenuVisible: boolean;
	readonly lastFetchedItemsFromPricing: string[];
	readonly shouldGroupByMPN: boolean;
}

class UnifiedSearchDocumentProductCatalogDrawerClass extends Component<
	Props,
	State
> {
	state: State = {
		loading: false,
		pricingLoading: false,
		searchQuery: "",
		dataSource: [],
		metaInfo: new ProductMetaInfo(),
		selectedProductList: [],
		currentPageNo: 1,
		hasMore: false,
		productVsPricing: {},
		filters: {
			source_types: [
				"self_catalog",
				"custom_distributor",
				"dicker_data",
				"icecat"
			],
			manufacturers: [],
			categories: [],
			icecat_category_ids: [],
			supplier_ids: [],
			exclude_out_of_stock: false,
			filter_by_connected_distributors: true,
			exclude_drop_ship_wh: false,
			item_types: [],
			sync_sources: []
		},
		showMpnSearch: false,
		mpnQuery: "",
		createProductButtonVisible: false,
		lastSearchItems: [],
		hasSearched: false,
		sortBy: undefined,
		currentItemSource: ProductSource.Icecat,
		amazonMetaInfo: new ProductMetaInfo(),
		amazonCursor: null,
		amazonFilterState: {
			includeOutOfStockProducts: [],
			eligibleForFreeShipping: false,
			amazonPrimeEligible: false,
			amazonProductCategory: [],
			amazonProductSubCategory: [],
			amazonProductDeliveryDay: []
		},
		amazonFilterMenuVisible: false,
		lastFetchedItemsFromPricing: [],
		shouldGroupByMPN: true
	};

	tableContainerRef = React.createRef<HTMLDivElement>();
	searchInputRef = React.createRef<any>();
	_isMounted = false;
	private scrollHandler = (event: Event) => this.onScroll(event);

	// Constants
	private readonly PAGE_SIZE_DEFAULT = 20;
	private readonly PAGE_SIZE_WITH_SORT = 50;
	private readonly SCROLL_THRESHOLD = 60;
	private readonly SCROLL_LISTENER_DELAY = 0;

	// Request tracking for cancellation
	private searchRequestId = 0;
	private pricingRequestIdsByPage = new Map<number, number>();
	private pagesLoadingPricing = new Set<number>();
	private mpnRequestId = 0;
	private amazonRequestId = 0;

	createProductsWithoutPricing = (items: UnifiedSearchItem[]): Product[] => {
		return items.map(item => ({
			id: item.item_id || item.identifier,
			name: item.name,
			description: item.description,
			item_images: item.images,
			manufacturer_part_no: item.mpn,
			identifier: item.identifier,
			product_source: item.source_type,
			pricing: [],
			unit_of_measure: UOM.EACH,
			product_type: ProductType.Hardware,
			created: "",
			updated: "",
			type: ProductType.Hardware,
			partner_id: "",
			company_id: "",
			product_number: null,
			logo: "",
			vendor_id: null,
			is_duplicate_manufacturer_part_no: false,
			mandatory_custom_field_check_require: false
		})) as unknown as Product[];
	};

	// Helper to compute sort key once per product (optimization)
	private computeSortKey = (
		product: Product,
		sortBy: SortOptions
	): number | string => {
		switch (sortBy) {
			case SortOptions.SellPriceHighToLow:
			case SortOptions.SellPriceLowToHigh: {
				if (product.pricing.length === 0) return 0;
				const prices = product.pricing.map(p => p.sell_price ?? 0);
				return sortBy === SortOptions.SellPriceLowToHigh
					? Math.min(...prices)
					: Math.max(...prices);
			}
			case SortOptions.CostPriceHighToLow:
			case SortOptions.CostPriceLowToHigh: {
				if (product.pricing.length === 0) return 0;
				const prices = product.pricing.map(p => p.cost_price ?? 0);
				return sortBy === SortOptions.CostPriceLowToHigh
					? Math.min(...prices)
					: Math.max(...prices);
			}
			case SortOptions.NameAToZ:
			case SortOptions.NameZToA:
				return product.name;
			default:
				return 0;
		}
	};

	sortDataSource = (
		data: Product[],
		sortBy: SortOptions | undefined
	): Product[] => {
		if (!sortBy) {
			return data;
		}

		// Pre-compute sort keys once (O(n) instead of O(n²))
		const dataWithKeys = data.map(product => ({
			product,
			sortKey: this.computeSortKey(product, sortBy)
		}));

		// Sort using pre-computed keys
		dataWithKeys.sort((a, b) => {
			const keyA = a.sortKey;
			const keyB = b.sortKey;

			// Handle numeric sorting
			if (typeof keyA === "number" && typeof keyB === "number") {
				// High to low: return b - a, Low to high: return a - b
				if (
					sortBy === SortOptions.SellPriceHighToLow ||
					sortBy === SortOptions.CostPriceHighToLow
				) {
					return keyB - keyA;
				}
				return keyA - keyB;
			}

			// Handle string sorting (names)
			if (typeof keyA === "string" && typeof keyB === "string") {
				return sortBy === SortOptions.NameZToA
					? keyB.localeCompare(keyA)
					: keyA.localeCompare(keyB);
			}

			return 0;
		});

		// Return sorted products
		return dataWithKeys.map(item => item.product);
	};

	componentDidMount() {
		this._isMounted = true;
		this.addScrollListener();
		if (!this.state.hasSearched) {
			this.performInitialSearch();
		}
	}

	componentWillUnmount() {
		this._isMounted = false;
		this.removeScrollListener();
	}

	performInitialSearch = () => {
		this.setState({
			loading: true
		});

		const initialFilters: UnifiedSearchFilters = {
			...this.state.filters,
			source_types: ["self_catalog"]
		};

		this.performSearch("", 1, initialFilters, true);
	};

	resetDrawerState = () => {
		// Clear request tracking
		this.searchRequestId = 0;
		this.pricingRequestIdsByPage.clear();
		this.pagesLoadingPricing.clear();
		this.mpnRequestId = 0;
		this.amazonRequestId = 0;

		this.setState({
			searchQuery: "",
			dataSource: [],
			metaInfo: new ProductMetaInfo(),
			hasSearched: false,
			loading: false,
			pricingLoading: false,
			currentPageNo: 1,
			hasMore: false,
			productVsPricing: {},
			filters: {
				source_types: [
					"self_catalog",
					"custom_distributor",
					"dicker_data",
					"icecat"
				],
				manufacturers: [],
				categories: [],
				icecat_category_ids: [],
				supplier_ids: [],
				exclude_out_of_stock: false,
				filter_by_connected_distributors: true,
				exclude_drop_ship_wh: false,
				item_types: [],
				sync_sources: []
			},
			showMpnSearch: false,
			mpnQuery: "",
			createProductButtonVisible: false,
			lastSearchItems: [],
			sortBy: undefined,
			currentItemSource: ProductSource.Icecat,
			amazonMetaInfo: new ProductMetaInfo(),
			amazonCursor: null,
			amazonFilterState: {
				includeOutOfStockProducts: [],
				eligibleForFreeShipping: false,
				amazonPrimeEligible: false,
				amazonProductCategory: [],
				amazonProductSubCategory: [],
				amazonProductDeliveryDay: []
			},
			lastFetchedItemsFromPricing: []
		});
	};

	addScrollListener = () => {
		setTimeout(() => {
			const drawer: HTMLElement | null | undefined =
				this.tableContainerRef.current?.querySelector(
					".table-page-container .product-table .ant-table"
				);
			if (drawer) {
				// Remove any existing listener first to prevent duplicates
				drawer.removeEventListener("scroll", this.scrollHandler);
				// Add new listener
				drawer.addEventListener("scroll", this.scrollHandler);
			}
		}, this.SCROLL_LISTENER_DELAY);
	};

	removeScrollListener = () => {
		const drawer: HTMLElement | null | undefined =
			this.tableContainerRef.current?.querySelector(
				".table-page-container .product-table .ant-table"
			);
		if (drawer) {
			drawer.removeEventListener("scroll", this.scrollHandler);
		}
	};

	resetScrollPosition = () => {
		setTimeout(() => {
			const drawer: HTMLElement | null | undefined =
				this.tableContainerRef.current?.querySelector(
					".table-page-container .product-table .ant-table"
				);
			if (drawer) {
				drawer.scrollTop = 0;
			}
		}, 100);
	};

	onScroll = (event: Event) => {
		const drawer = event.target as HTMLElement;

		if (!drawer || this.state.loading) {
			return;
		}

		const { currentItemSource, amazonCursor, hasMore } = this.state;

		const isAmazonIntegrated =
			currentItemSource === ProductSource.Amazon &&
			this.props.isAmazonDistributorIntegrated;

		if (isAmazonIntegrated && !amazonCursor) {
			return;
		}

		if (!isAmazonIntegrated && !hasMore) {
			return;
		}

		const isAtBottom =
			drawer.offsetHeight + drawer.scrollTop >=
			drawer.scrollHeight - this.SCROLL_THRESHOLD;

		if (!isAtBottom) {
			return;
		}

		if (isAmazonIntegrated) {
			this.searchAmazonProducts(true);
		} else {
			this.loadMoreProducts();
		}
	};

	getPageSize = (): number => {
		return this.state.sortBy
			? this.PAGE_SIZE_WITH_SORT
			: this.PAGE_SIZE_DEFAULT;
	};

	loadMoreProducts = async () => {
		if (this.state.loading || !this.state.hasMore) {
			return;
		}

		if (this.state.sortBy && this.state.currentPageNo >= 2) {
			return;
		}

		const nextPageNo = this.state.currentPageNo + 1;
		await this.handleSearch(this.state.searchQuery, nextPageNo);
	};

	onProductSourceChange = (currentItemSource: ProductSource) => {
		cache.save("product_search_content_source", currentItemSource);

		// Clear request tracking
		this.searchRequestId = 0;
		this.pricingRequestIdsByPage.clear();
		this.pagesLoadingPricing.clear();
		this.mpnRequestId = 0;
		this.amazonRequestId = 0;

		this.setState({
			currentItemSource,
			searchQuery: "",
			dataSource: [],
			hasSearched: false,
			loading: false,
			pricingLoading: false,
			selectedProductList: [],
			productVsPricing: {},
			metaInfo: new ProductMetaInfo(),
			amazonMetaInfo: new ProductMetaInfo(),
			amazonCursor: null,
			amazonFilterState: {
				includeOutOfStockProducts: [],
				eligibleForFreeShipping: false,
				amazonPrimeEligible: false,
				amazonProductCategory: [],
				amazonProductSubCategory: [],
				amazonProductDeliveryDay: []
			},
			lastFetchedItemsFromPricing: []
		});
		this.resetScrollPosition();
	};

	performSearch = async (
		query: string,
		pageNo: number = 1,
		filterOverrides?: UnifiedSearchFilters,
		isInitialLoad: boolean = false
	) => {
		// Only increment searchRequestId for new searches, not pagination
		if (pageNo === 1) {
			++this.searchRequestId;
		}
		const currentSearchId = this.searchRequestId;

		this.setState({
			loading: true,
			pricingLoading: pageNo === 1 ? false : this.state.pricingLoading,
			currentPageNo: pageNo,
			selectedProductList:
				pageNo === 1 ? [] : this.state.selectedProductList,
			productVsPricing: pageNo === 1 ? {} : this.state.productVsPricing,
			dataSource: pageNo === 1 ? [] : this.state.dataSource
		});

		try {
			const searchReq: UnifiedSearchRequest = {
				query,
				filters: filterOverrides || this.state.filters,
				page_no: pageNo,
				page_size: this.getPageSize(),
				should_group_by_mpn: this.state.shouldGroupByMPN
			};

			if (pageNo === 1) {
				this.pricingRequestIdsByPage.clear();
				this.pagesLoadingPricing.clear();

				const [searchResp, aggResp] = await Promise.all([
					searchUnifiedProducts(searchReq),
					getUnifiedProductsAggregations({
						query,
						filters: buildAggregationFilters(
							filterOverrides || this.state.filters
						)
					})
				]);

				// Check if this request is still current
				if (currentSearchId !== this.searchRequestId) return;

				if (searchResp.items.length === 0) {
					if (!this._isMounted) return;
					this.setState({
						showMpnSearch:
							!isInitialLoad &&
							this.state.currentItemSource !==
								ProductSource.Amazon,
						loading: false,
						dataSource: [],
						hasSearched: !isInitialLoad,
						searchQuery: query
					});
					return;
				}

				const productsWithoutPricing =
					this.createProductsWithoutPricing(searchResp.items);

				const metaInfo = convertAggregationsToMetaInfo(aggResp);

				if (!this._isMounted) return;

				this.pagesLoadingPricing.add(pageNo);

				this.setState({
					dataSource: productsWithoutPricing,
					metaInfo,
					hasMore: !!searchResp.next,
					currentPageNo: pageNo,
					loading: false,
					pricingLoading: true,
					showMpnSearch: false,
					lastSearchItems: searchResp.items,
					hasSearched: !isInitialLoad,
					searchQuery: query,
					productVsPricing: {}
				});

				this.addScrollListener();

				const { product_search_response_infos, self_catalog_ids } =
					prepareIdentifiersForPricing(searchResp.items);

				const pricingReq: UnifiedPricingRequest = {
					product_search_response_infos,
					self_catalog_ids,
					filters: filterOverrides || this.state.filters,
					page_no: pageNo,
					page_size: this.getPageSize(),
					allowed_direct_distributor_search: false,
					should_group_by_mpn: this.state.shouldGroupByMPN
				};

				const currentPricingId =
					(this.pricingRequestIdsByPage.get(pageNo) || 0) + 1;
				this.pricingRequestIdsByPage.set(pageNo, currentPricingId);

				const pricingResp = await searchUnifiedProductsPricing(
					pricingReq
				);

				// Check if this request is still current
				if (
					currentSearchId !== this.searchRequestId ||
					currentPricingId !==
						this.pricingRequestIdsByPage.get(pageNo)
				)
					return;

				let products = flattenPricingResponseToProducts(
					pricingResp,
					searchResp.items
				);

				products = this.sortDataSource(products, this.state.sortBy);

				const productVsPricingInitialValue = selectPricingBasedOnSort(
					products,
					this.state.sortBy
				);

				if (!this._isMounted) return;

				this.pagesLoadingPricing.delete(pageNo);

				this.setState({
					dataSource: products,
					pricingLoading: this.pagesLoadingPricing.size > 0,
					productVsPricing: productVsPricingInitialValue
				});
			} else {
				const searchResp = await searchUnifiedProducts(searchReq);

				// Check if this request is still current
				if (currentSearchId !== this.searchRequestId) return;

				if (searchResp.items.length === 0) {
					if (!this._isMounted) return;
					this.setState({
						loading: false,
						hasMore: false
					});
					return;
				}

				const productsWithoutPricing =
					this.createProductsWithoutPricing(searchResp.items);

				if (!this._isMounted) return;

				this.pagesLoadingPricing.add(pageNo);

				this.setState(prevState => ({
					dataSource: [
						...prevState.dataSource,
						...productsWithoutPricing
					],
					hasMore: !!searchResp.next,
					currentPageNo: pageNo,
					loading: false,
					pricingLoading: true,
					lastSearchItems: [
						...prevState.lastSearchItems,
						...searchResp.items
					]
				}));

				const { product_search_response_infos, self_catalog_ids } =
					prepareIdentifiersForPricing(searchResp.items);

				const currentPricingId =
					(this.pricingRequestIdsByPage.get(pageNo) || 0) + 1;
				this.pricingRequestIdsByPage.set(pageNo, currentPricingId);

				const pricingResp = await searchUnifiedProductsPricing({
					product_search_response_infos,
					self_catalog_ids,
					filters: filterOverrides || this.state.filters,
					page_no: pageNo,
					page_size: this.getPageSize(),
					allowed_direct_distributor_search: false,
					should_group_by_mpn: this.state.shouldGroupByMPN
				});

				// Check if this request is still current
				if (
					currentSearchId !== this.searchRequestId ||
					currentPricingId !==
						this.pricingRequestIdsByPage.get(pageNo)
				)
					return;
				const newProducts = flattenPricingResponseToProducts(
					pricingResp,
					searchResp.items
				);

				const pricingMapForDataSource =
					constructProductIdToPricingMapping(newProducts);
				const productVsPricingUpdates = Object.keys(
					pricingMapForDataSource
				).reduce((map, productId) => {
					map[productId] = pricingMapForDataSource[productId];
					return map;
				}, {} as Record<string, string | null>);

				if (!this._isMounted) return;

				this.pagesLoadingPricing.delete(pageNo);

				this.setState(prevState => {
					const pageSize = this.getPageSize();
					const startIndex = (pageNo - 1) * pageSize;

					// Create a copy of dataSource and replace this page's products
					const updatedDataSource = [...prevState.dataSource];
					updatedDataSource.splice(
						startIndex,
						newProducts.length,
						...newProducts
					);

					// Re-sort entire list if sorting is enabled
					const finalDataSource = this.sortDataSource(
						updatedDataSource,
						this.state.sortBy
					);

					return {
						dataSource: finalDataSource,
						pricingLoading: this.pagesLoadingPricing.size > 0,
						productVsPricing: {
							...prevState.productVsPricing,
							...productVsPricingUpdates
						}
					};
				});
			}
		} catch (error) {
			console.error("performSearch error:", error);
			const errorMessage =
				error instanceof Error ? error.message : "Unknown error";
			ZMessage.error(`Failed to search products: ${errorMessage}`);
			if (!this._isMounted) return;
			this.setState({
				loading: false,
				pricingLoading: false,
				currentPageNo: pageNo > 1 ? pageNo - 1 : 1
			});
		}
	};

	handleSearch = async (query: string, pageNo: number = 1) => {
		if (!query && pageNo === 1) {
			return;
		}

		const { currentItemSource } = this.state;

		const isAmazonIntegrated =
			currentItemSource === ProductSource.Amazon &&
			this.props.isAmazonDistributorIntegrated;

		if (isAmazonIntegrated) {
			this.searchAmazonProducts(false);
			return;
		}

		// Only increment searchRequestId for new searches, not pagination
		if (pageNo === 1) {
			++this.searchRequestId;
		}
		const currentSearchId = this.searchRequestId;

		this.setState({
			loading: true,
			pricingLoading: pageNo === 1 ? false : this.state.pricingLoading,
			currentPageNo: pageNo,
			selectedProductList:
				pageNo === 1 ? [] : this.state.selectedProductList,
			productVsPricing: pageNo === 1 ? {} : this.state.productVsPricing,
			dataSource: pageNo === 1 ? [] : this.state.dataSource
		});

		if (pageNo === 1) {
			this.resetScrollPosition();
		}

		try {
			const searchReq: UnifiedSearchRequest = {
				query,
				filters: this.state.filters,
				page_no: pageNo,
				page_size: this.getPageSize(),
				should_group_by_mpn: this.state.shouldGroupByMPN
			};

			if (pageNo === 1) {
				this.pricingRequestIdsByPage.clear();
				this.pagesLoadingPricing.clear();

				const [searchResp, aggResp] = await Promise.all([
					searchUnifiedProducts(searchReq),
					getUnifiedProductsAggregations({
						query,
						filters: buildAggregationFilters(this.state.filters)
					})
				]);

				// Check if this request is still current
				if (currentSearchId !== this.searchRequestId) return;

				if (searchResp.items.length === 0) {
					if (!this._isMounted) return;
					this.setState({
						showMpnSearch:
							this.state.currentItemSource !==
							ProductSource.Amazon,
						loading: false,
						dataSource: [],
						hasSearched: true,
						searchQuery: query
					});
					return;
				}

				// Show products immediately with empty pricing (triggers skeleton loaders)
				const productsWithoutPricing =
					this.createProductsWithoutPricing(searchResp.items);

				const metaInfo = convertAggregationsToMetaInfo(aggResp);

				if (!this._isMounted) return;

				this.pagesLoadingPricing.add(pageNo);

				this.setState({
					dataSource: productsWithoutPricing,
					metaInfo,
					hasMore: !!searchResp.next,
					currentPageNo: pageNo,
					loading: false,
					pricingLoading: true,
					showMpnSearch: false,
					lastSearchItems: searchResp.items,
					hasSearched: true,
					searchQuery: query,
					productVsPricing: {}
				});

				this.addScrollListener();

				// Fetch pricing in background
				const { product_search_response_infos, self_catalog_ids } =
					prepareIdentifiersForPricing(searchResp.items);

				const pricingReq: UnifiedPricingRequest = {
					product_search_response_infos,
					self_catalog_ids,
					filters: this.state.filters,
					page_no: pageNo,
					page_size: this.getPageSize(),
					allowed_direct_distributor_search: false,
					should_group_by_mpn: this.state.shouldGroupByMPN
				};

				const currentPricingId =
					(this.pricingRequestIdsByPage.get(pageNo) || 0) + 1;
				this.pricingRequestIdsByPage.set(pageNo, currentPricingId);

				const pricingResp = await searchUnifiedProductsPricing(
					pricingReq
				);

				// Check if this request is still current
				if (
					currentSearchId !== this.searchRequestId ||
					currentPricingId !==
						this.pricingRequestIdsByPage.get(pageNo)
				)
					return;
				let products = flattenPricingResponseToProducts(
					pricingResp,
					searchResp.items
				);

				products = this.sortDataSource(products, this.state.sortBy);

				const productVsPricingInitialValue = selectPricingBasedOnSort(
					products,
					this.state.sortBy
				);

				if (!this._isMounted) return;

				this.pagesLoadingPricing.delete(pageNo);

				this.setState({
					dataSource: products,
					pricingLoading: this.pagesLoadingPricing.size > 0,
					productVsPricing: productVsPricingInitialValue
				});
			} else {
				const searchResp = await searchUnifiedProducts(searchReq);

				// Check if this request is still current
				if (currentSearchId !== this.searchRequestId) return;

				if (searchResp.items.length === 0) {
					if (!this._isMounted) return;
					this.setState({
						loading: false,
						hasMore: false
					});
					return;
				}

				// Show products immediately with empty pricing (triggers skeleton loaders)
				const productsWithoutPricing =
					this.createProductsWithoutPricing(searchResp.items);

				if (!this._isMounted) return;

				this.pagesLoadingPricing.add(pageNo);

				this.setState(prevState => ({
					dataSource: [
						...prevState.dataSource,
						...productsWithoutPricing
					],
					hasMore: !!searchResp.next,
					currentPageNo: pageNo,
					loading: false,
					pricingLoading: true,
					lastSearchItems: [
						...prevState.lastSearchItems,
						...searchResp.items
					]
				}));

				// Fetch pricing in background
				const { product_search_response_infos, self_catalog_ids } =
					prepareIdentifiersForPricing(searchResp.items);

				const currentPricingId =
					(this.pricingRequestIdsByPage.get(pageNo) || 0) + 1;
				this.pricingRequestIdsByPage.set(pageNo, currentPricingId);

				const pricingResp = await searchUnifiedProductsPricing({
					product_search_response_infos,
					self_catalog_ids,
					filters: this.state.filters,
					page_no: pageNo,
					page_size: this.getPageSize(),
					allowed_direct_distributor_search: false,
					should_group_by_mpn: this.state.shouldGroupByMPN
				});

				const newProducts = flattenPricingResponseToProducts(
					pricingResp,
					searchResp.items
				);

				// Check if this request is still current
				if (
					currentSearchId !== this.searchRequestId ||
					currentPricingId !==
						this.pricingRequestIdsByPage.get(pageNo)
				)
					return;

				const productVsPricingUpdates = selectPricingBasedOnSort(
					newProducts,
					this.state.sortBy
				);

				if (!this._isMounted) return;

				this.pagesLoadingPricing.delete(pageNo);

				// Replace skeleton products with real data for this specific page
				this.setState(prevState => {
					const pageSize = this.getPageSize();
					const startIndex = (pageNo - 1) * pageSize;

					// Create a copy of dataSource and replace this page's products
					const updatedDataSource = [...prevState.dataSource];
					updatedDataSource.splice(
						startIndex,
						newProducts.length,
						...newProducts
					);

					// Re-sort entire list if sorting is enabled
					const finalDataSource = this.sortDataSource(
						updatedDataSource,
						this.state.sortBy
					);

					return {
						dataSource: finalDataSource,
						pricingLoading: this.pagesLoadingPricing.size > 0,
						productVsPricing: {
							...prevState.productVsPricing,
							...productVsPricingUpdates
						}
					};
				});
			}
		} catch (error) {
			console.error("handleSearch error:", error);
			const errorMessage =
				error instanceof Error ? error.message : "Unknown error";
			ZMessage.error(`Failed to search products: ${errorMessage}`);
			if (!this._isMounted) return;
			this.setState({
				loading: false,
				pricingLoading: false,
				currentPageNo: pageNo > 1 ? pageNo - 1 : 1
			});
		}
	};

	searchAmazonProducts = async (isLazyLoad = false) => {
		const { searchQuery, amazonCursor, amazonFilterState } = this.state;

		if (!searchQuery.trim()) {
			ZMessage.error("Please enter a search term");
			return;
		}

		const currentAmazonId = ++this.amazonRequestId;

		try {
			this.setState({
				loading: true,
				hasSearched: true
			});

			if (this.props.updateAmazonAppliedFilters) {
				const appliedFilters: AmazonProductAppliedFilters = {
					categories:
						amazonFilterState.amazonProductCategory?.map(
							catValue => {
								const categoryOption =
									this.state.amazonMetaInfo.categories.find(
										c => c.value === catValue
									);
								const previousOption =
									!categoryOption &&
									this.props.amazonAppliedFilters
										? this.props.amazonAppliedFilters.categories.find(
												c => c.value === catValue
										  )
										: null;
								const finalOption =
									categoryOption || previousOption;
								return {
									key: catValue,
									value: catValue,
									label: finalOption?.label || catValue,
									count: finalOption?.count || 0
								};
							}
						) ?? [],
					sub_categories:
						amazonFilterState.amazonProductSubCategory?.map(
							subCatValue => {
								const subCategoryOption =
									this.state.amazonMetaInfo.sub_categories.find(
										sc => sc.value === subCatValue
									);
								const previousOption =
									!subCategoryOption &&
									this.props.amazonAppliedFilters
										? this.props.amazonAppliedFilters.sub_categories.find(
												sc => sc.value === subCatValue
										  )
										: null;
								const finalOption =
									subCategoryOption || previousOption;
								return {
									key: subCatValue,
									value: subCatValue,
									label: finalOption?.label || subCatValue,
									count: finalOption?.count || 0
								};
							}
						) ?? [],
					availability_options:
						amazonFilterState.includeOutOfStockProducts?.map(
							availValue => {
								const availabilityOption =
									this.state.amazonMetaInfo.amazon_refinements?.availability_options?.find(
										a => a.value === availValue
									);
								const previousOption =
									!availabilityOption &&
									this.props.amazonAppliedFilters
										? this.props.amazonAppliedFilters.availability_options.find(
												a => a.value === availValue
										  )
										: null;
								const finalOption =
									availabilityOption || previousOption;
								return {
									key: availValue,
									value: availValue,
									label: finalOption?.label || availValue,
									count: finalOption?.count || 0
								};
							}
						) ?? []
				};
				this.props.updateAmazonAppliedFilters(appliedFilters);
			}

			const amazonRefineMentsMetaData =
				this.state.amazonMetaInfo.amazon_refinements;
			const amazonRefinements: AmazonRefinementsFilters = {
				amazon_prime_eligible: amazonFilterState.amazonPrimeEligible
					? amazonRefineMentsMetaData?.prime_eligible[0]?.value || ""
					: "",
				eligible_for_free_shipping:
					amazonFilterState.eligibleForFreeShipping
						? amazonRefineMentsMetaData
								?.eligible_for_free_shipping_options[0]
								?.value || ""
						: "",
				include_out_of_stock_products:
					amazonFilterState.includeOutOfStockProducts?.toString() ??
					"",
				amazon_delivery_date:
					amazonFilterState.amazonProductDeliveryDay?.toString() ?? ""
			};

			const searchRequest = ProductSearchRequest.constructSearchRequest(
				searchQuery,
				ProductSource.Amazon,
				isLazyLoad ? amazonCursor : null,
				true,
				false,
				[],
				[],
				false,
				false,
				[],
				amazonFilterState.amazonProductCategory || [],
				[],
				[],
				undefined,
				isLazyLoad,
				undefined,
				amazonFilterState.amazonProductSubCategory || [],
				amazonRefinements
			);

			const response = await searchProducts(searchRequest);

			if (currentAmazonId !== this.amazonRequestId) return;
			if (!this._isMounted) return;

			const dataSourceValue = isLazyLoad
				? [...this.state.dataSource, ...response.data]
				: response.data;

			const productVsPricingMap =
				constructProductIdToPricingMapping(dataSourceValue);

			this.setState({
				dataSource: dataSourceValue,
				amazonMetaInfo: response.meta_info || new ProductMetaInfo(),
				amazonCursor: response.next || null,
				productVsPricing: {
					...this.state.productVsPricing,
					...productVsPricingMap
				},
				loading: false
			});
		} catch (error) {
			console.error("Amazon search error:", error);
			ZMessage.error("Failed to search Amazon products");
			if (!this._isMounted) return;
			this.setState({ loading: false });
		}
	};

	handleMpnSearch = async (mpn: string) => {
		if (!mpn) {
			ZMessage.warning("Please enter an MPN number");
			return;
		}

		const currentMpnId = ++this.mpnRequestId;

		this.setState({ loading: true });

		try {
			const pricingReq: UnifiedPricingRequest = {
				direct_distributor_search_query: mpn,
				product_search_response_infos: [],
				self_catalog_ids: [],
				filters: this.state.filters,
				page_no: 1,
				page_size: this.getPageSize(),
				allowed_direct_distributor_search: true,
				should_group_by_mpn: this.state.shouldGroupByMPN
			};

			const pricingResp = await searchUnifiedProductsPricing(pricingReq);

			if (currentMpnId !== this.mpnRequestId) return;

			const products = flattenPricingResponseToProducts(pricingResp, []);

			if (products.length === 0) {
				ZMessage.warning("No products found for this MPN");
				if (!this._isMounted) return;
				this.setState({
					loading: false,
					dataSource: [],
					showMpnSearch: true,
					mpnQuery: ""
				});
				return;
			}

			const pricingMapForDataSource =
				constructProductIdToPricingMapping(products);
			const productVsPricingInitialValue = Object.keys(
				pricingMapForDataSource
			).reduce((map, productId) => {
				map[productId] = pricingMapForDataSource[productId];
				return map;
			}, {} as Record<string, string | null>);

			if (!this._isMounted) return;

			this.setState({
				dataSource: products,
				hasMore: false,
				showMpnSearch: false,
				loading: false,
				searchQuery: "",
				productVsPricing: productVsPricingInitialValue
			});
		} catch (error) {
			console.error("performMpnSearch error:", error);
			const errorMessage =
				error instanceof Error ? error.message : "Unknown error";
			ZMessage.error(`Failed to search by MPN: ${errorMessage}`);
			if (!this._isMounted) return;
			this.setState({
				loading: false,
				dataSource: [],
				showMpnSearch: true
			});
		}
	};

	handleFiltersApply = (
		manufacturers: string[],
		categories: string[],
		icecatCategories: string[],
		productTypes: string[],
		suppliers: string[],
		syncSources: string[],
		excludeOutOfStock: boolean,
		filterByConnectedDistributors: boolean,
		excludeDropShipWH: boolean
	) => {
		const updatedFilters: UnifiedSearchFilters = {
			...this.state.filters,
			manufacturers: manufacturers,
			categories: categories,
			icecat_category_ids: icecatCategories,
			supplier_ids: suppliers,
			exclude_out_of_stock: excludeOutOfStock,
			filter_by_connected_distributors: filterByConnectedDistributors,
			exclude_drop_ship_wh: excludeDropShipWH,
			item_types: productTypes,
			sync_sources: syncSources
		};

		this.setState(
			{
				filters: updatedFilters,
				currentPageNo: 1,
				dataSource: [],
				selectedProductList: [],
				productVsPricing: {},
				hasMore: true
			},
			() => {
				this.performSearch(this.state.searchQuery, 1);
				this.resetScrollPosition();
			}
		);
	};

	handleAmazonFilterChange = (values: string[]) => (key: string) => {
		const selectedValue = !!values[0] ? values : [];
		let updatedFilterState = { ...this.state.amazonFilterState };

		if (key === AmazonCatalogFilterKeys.FilterByProductCategory) {
			updatedFilterState = {
				...updatedFilterState,
				amazonProductCategory: selectedValue
			};
		} else if (key === AmazonCatalogFilterKeys.FilterBySubCategory) {
			updatedFilterState = {
				...updatedFilterState,
				amazonProductSubCategory: selectedValue
			};
		} else if (key === AmazonCatalogFilterKeys.FilterByDeliveryDay) {
			updatedFilterState = {
				...updatedFilterState,
				amazonProductDeliveryDay: selectedValue
			};
		} else if (key === AmazonCatalogFilterKeys.FilterByAvailability) {
			updatedFilterState = {
				...updatedFilterState,
				includeOutOfStockProducts: selectedValue
			};
		}

		this.setState({ amazonFilterState: updatedFilterState });
	};

	handleAmazonFreeShippingChange = (e: CheckboxChangeEvent) => {
		this.setState({
			amazonFilterState: {
				...this.state.amazonFilterState,
				eligibleForFreeShipping: e.target.checked
			}
		});
	};

	handleAmazonPrimeEligibleChange = (e: CheckboxChangeEvent) => {
		this.setState({
			amazonFilterState: {
				...this.state.amazonFilterState,
				amazonPrimeEligible: e.target.checked
			}
		});
	};

	handleAmazonFilterVisibilityChange = (visible: boolean) => {
		this.setState({ amazonFilterMenuVisible: visible });
	};

	handleClearAmazonFilters = () => {
		this.setState({
			amazonFilterState: {
				includeOutOfStockProducts: [],
				eligibleForFreeShipping: false,
				amazonPrimeEligible: false,
				amazonProductCategory: [],
				amazonProductSubCategory: [],
				amazonProductDeliveryDay: []
			}
		});
	};

	onApplyAmazonFilters = () => {
		this.setState(
			{
				amazonFilterMenuVisible: false,
				dataSource: [],
				amazonCursor: null
			},
			() => {
				this.searchAmazonProducts(false);
			}
		);
	};

	handleSortingOptionChange = (value: SortOptions) => {
		this.setState(
			{
				sortBy: value,
				currentPageNo: 1,
				dataSource: [],
				productVsPricing: {},
				selectedProductList: [],
				hasMore: false
			},
			() => {
				if (this.state.searchQuery) {
					this.handleSearch(this.state.searchQuery, 1);
				}
				this.resetScrollPosition();
			}
		);
	};

	onPricingSelect = (productId: string, selectedPricingId?: string) => {
		this.setState({
			productVsPricing: {
				...this.state.productVsPricing,
				[productId]: selectedPricingId || null
			}
		});
	};

	handleProductRowEvent = (record: Product) => ({
		onClick: () => {
			if (record.product_source === ProductSource.ZomentumMarketplace) {
				const currentProductVendorStatus =
					this.props.vendorProgramDetails?.find(v => {
						return v.id === record.vendor_company_id;
					})?.status ?? VendorSubscriptionStatus.NOTAPPLIED;
				if (
					currentProductVendorStatus !==
					VendorSubscriptionStatus.ACCEPTED
				)
					return;
			}
			this.props.itemType === ProductType.Bundle
				? this.setState({ selectedProductList: [record] })
				: this.setState(prevState => ({
						selectedProductList: prevState.selectedProductList.some(
							product => product.id === record.id
						)
							? prevState.selectedProductList.filter(
									product => product.id !== record.id
							  )
							: [...prevState.selectedProductList, record]
				  }));
		},
		onDoubleClick: () =>
			this.props.itemType === ProductType.Bundle
				? this.submitProductData
				: null
	});

	handleCreateProductClick = () => {
		this.setState({ createProductButtonVisible: true });
	};

	handleOnClose = () => this.setState({ createProductButtonVisible: false });

	handleCreateProductAdded = (addedProduct: Product) => {
		const updatedDataSource = [addedProduct, ...this.state.dataSource];

		const updatedProductVsPricing = {
			...this.state.productVsPricing,
			[addedProduct.id]: getPreferredPricingForProduct(addedProduct)
		};

		this.setState({
			createProductButtonVisible: false,
			dataSource: updatedDataSource,
			productVsPricing: { ...updatedProductVsPricing }
		});
	};

	handleGroupByMPNChange = (e: CheckboxChangeEvent) => {
		this.setState(
			{
				shouldGroupByMPN: e.target.checked,
				currentPageNo: 1,
				dataSource: [],
				selectedProductList: [],
				productVsPricing: {},
				hasMore: true
			},
			() => {
				if (this.state.searchQuery) {
					this.handleSearch(this.state.searchQuery, 1);
				}
				this.resetScrollPosition();
			}
		);
	};

	removeItemsFromUpdateMandatoryFieldsDrawer = (products: Product[]) => {
		this.props.removeQuoteProducts?.(products.map(product => product.id));
		this.setState(prevState => ({
			selectedProductList: prevState.selectedProductList.filter(
				product =>
					!products.some(
						productToBeRemoved =>
							product.id === productToBeRemoved.id
					)
			)
		}));
	};

	onSubmit = ($event: React.MouseEvent<HTMLElement, MouseEvent>) => {
		$event.preventDefault();
		if (
			!(
				this.state.selectedProductList &&
				this.state.selectedProductList.length > 0
			)
		) {
			ZMessage.error("Please select a product");
			return;
		}

		if (this.state.pricingLoading) {
			ZMessage.warning(
				"Product pricing is still loading. Please wait a moment."
			);
			return;
		}

		const selectedProductId = this.state.selectedProductList[0].id;

		const currentProductData = this.state.dataSource.find(
			product => product.id === selectedProductId
		);

		if (!currentProductData) {
			const orphanedSelections = this.state.selectedProductList.filter(
				p => !this.state.dataSource.find(d => d.id === p.id)
			);

			if (orphanedSelections.length > 0) {
				this.setState({
					selectedProductList: this.state.selectedProductList.filter(
						p => this.state.dataSource.find(d => d.id === p.id)
					)
				});
				ZMessage.error(
					"The selected product is no longer in the current results. Please search again and select a product."
				);
			} else {
				ZMessage.error(
					"Unable to find product data. Please try refreshing your search."
				);
			}
			return;
		}

		const hasNoPricing =
			!currentProductData.pricing ||
			currentProductData.pricing.length === 0;

		if (hasNoPricing) {
			ZModal.confirm({
				title: "No Pricing Available",
				content:
					"This product does not have pricing information. You can still add it, but you'll need to enter pricing manually. Do you want to continue?",
				okText: "Add Without Pricing",
				cancelText: "Cancel",
				onOk: () => {
					this.proceedWithProductAddition(
						currentProductData,
						selectedProductId
					);
				}
			});
			return;
		}

		this.proceedWithProductAddition(currentProductData, selectedProductId);
	};

	proceedWithProductAddition = (
		currentProductData: Product,
		selectedProductId: string
	) => {
		const productToBeAdded = {
			...currentProductData,
			unit_of_measure: currentProductData.unit_of_measure || UOM.EACH,
			selectedPricingId: this.state.productVsPricing[selectedProductId],
			...(selectedProductId.startsWith("custom_distributor:") &&
				this.state.productVsPricing[selectedProductId] && {
					selectedPricing: currentProductData.pricing?.find(
						(p: any) =>
							p.id ===
							this.state.productVsPricing[selectedProductId]
					)
				})
		};

		if (this.props.updateSelectedProduct) {
			this.props.updateSelectedProduct(productToBeAdded);
		}
		this.submitProductData();
	};

	submitProductData = () => {
		const productList = !this.state.pricingLoading
			? this.state.selectedProductList.map(product => {
					const productWithPricing = this.state.dataSource.find(
						productWithPricing => {
							return product.id === productWithPricing.id;
						}
					);
					return productWithPricing || product;
			  })
			: this.state.selectedProductList;
		this.props.onSubmit(
			productList,
			this.state.productVsPricing,
			this.props.itemType === ProductType.Bundle
		);
	};

	renderProductColumn =
		(showUpdateMandatoryFieldsColumn: boolean) =>
		(_text: unknown, record: Product): ReactNode => {
			const selectedPricingId = this.state.productVsPricing[record.id];
			return (
				<ProductCatalogResultRow
					record={record}
					showPricing={!showUpdateMandatoryFieldsColumn}
					selectedPricingId={selectedPricingId}
					onPricingChange={this.onPricingSelect}
					isFromProductsPage={this.props.isFromProductsPage}
					currentItemSource={
						record.product_source || this.state.currentItemSource
					}
					pricingLoading={this.state.pricingLoading}
					showUpdateMandatoryFieldsColumn={
						showUpdateMandatoryFieldsColumn
					}
				>
					{showUpdateMandatoryFieldsColumn && (
						<ZUpdateProductCatalogResultColumn
							onUpdateFieldClick={() =>
								this.props.configureQuoteProduct?.(record.id)
							}
							onRemoveItem={() =>
								this.removeItemsFromUpdateMandatoryFieldsDrawer(
									[record]
								)
							}
							updated={
								!this.props.unfilledSelectedQuoteProductIds?.includes(
									record.id
								)
							}
							selectedPricingId={
								this.state.productVsPricing[record.id]
							}
							record={record}
							showPricing={showUpdateMandatoryFieldsColumn}
							currentItemSource={
								record.product_source ||
								this.state.currentItemSource
							}
						/>
					)}
				</ProductCatalogResultRow>
			);
		};

	productColumns: ColumnType<Product>[] = [
		{
			dataIndex: "name",
			key: "name",
			render: this.renderProductColumn(false)
		}
	];

	customEmptyProductsTable = () => {
		if (!this.state.hasSearched) {
			return (
				<div className="flex flex-col items-center">
					<SearchSomethingProductsIcon className="empty-table-icon" />
					<div className="empty-table-text">
						Start searching for products
					</div>
				</div>
			);
		}

		if (
			this.state.hasSearched &&
			this.state.dataSource.length === 0 &&
			this.state.currentItemSource === ProductSource.Amazon
		) {
			return (
				<div className="flex flex-col items-center">
					<ZIcon
						isIllustration
						name={EIllustrations.NO_RESULT_FOUND_BOX}
						className="empty-table-icon"
					/>
					<div className="empty-table-text">
						No results found. Try different search terms or adjust
						filters.
					</div>
				</div>
			);
		}

		if (this.state.showMpnSearch) {
			return (
				<div className="flex flex-col items-center unified-mpn-search-container">
					<ZIcon
						isIllustration
						name={EIllustrations.NO_RESULT_FOUND_BOX}
						className="empty-table-icon"
					/>
					<div className="empty-table-text">
						No result found for this query, try searching with MPN
						Number for accurate results from distributors or change
						the filters
					</div>

					<div className="mpn-search-section">
						<Input.Search
							placeholder="Enter MPN here"
							value={this.state.mpnQuery}
							onChange={e =>
								this.setState({ mpnQuery: e.target.value })
							}
							onSearch={() =>
								this.handleMpnSearch(this.state.mpnQuery)
							}
							enterButton={
								<Button
									type="primary"
									icon={<SearchOutlined />}
									loading={this.state.loading}
								>
									Search
								</Button>
							}
						/>
					</div>

					<div className="create-product-cta margin-top-8px">
						<ZText colorVariant={7}>OR</ZText>
						<Button
							block
							type="link"
							htmlType="button"
							onClick={this.handleCreateProductClick}
						>
							Create product in your self catalog
						</Button>
					</div>
				</div>
			);
		}

		return (
			<div className="flex flex-col items-center">
				<NoProductFoundIcon className="empty-table-icon" />
				<div className="empty-table-text">
					No products found for "{this.state.searchQuery}"
				</div>
				<div className="create-product-cta margin-top-8px">
					<ZText colorVariant={7}>OR</ZText>
					<Button
						block
						type="link"
						htmlType="button"
						onClick={this.handleCreateProductClick}
					>
						Create product in your self catalog
					</Button>
				</div>
			</div>
		);
	};

	renderTableHeader = (): React.ReactNode => {
		const isSearchDisabled =
			this.state.currentItemSource === ProductSource.Amazon &&
			!this.props.isAmazonDistributorIntegrated;

		return (
			<Row className="document-product-drawer-product-header w-full">
				<Col
					span={6}
					onClick={e => e && e.stopPropagation()}
					className="document-product-drawer-product-source w-full"
				>
					<Select<ProductSource>
						getPopupContainer={trigger => trigger.parentNode}
						allowClear={false}
						loading={this.props.isLoading || this.state.loading}
						disabled={
							this.state.loading || this.state.pricingLoading
						}
						value={this.state.currentItemSource}
						placeholder="Please choose the product source"
						onChange={this.onProductSourceChange}
						dropdownClassName="w-min"
						dropdownRender={menu => (
							<div>
								{menu}
								<ZFeatureAccess
									requiredRole={UserRole.SecondaryRole}
									requiredPlan={PricingPlanNames.IGNITE}
									permissions={
										this.props.createProductPermission
									}
								>
									<div
										className="create-product-dropdown-cta"
										onClick={this.handleCreateProductClick}
									>
										Create Product
									</div>
								</ZFeatureAccess>
							</div>
						)}
						className="w-full"
					>
						<Select.Option value={ProductSource.Icecat}>
							Global Catalogue
						</Select.Option>
						{this.props.itemType !== ProductType.Bundle &&
							this.props.hasPermissionForAmazonDistributor && (
								<Select.Option value={ProductSource.Amazon}>
									Amazon Business
								</Select.Option>
							)}
					</Select>
				</Col>
				<Col
					span={18}
					className="document-product-drawer-product-button"
				>
					<Input.Search
						ref={this.searchInputRef}
						value={this.state.searchQuery}
						onChange={e => {
							this.setState({ searchQuery: e.target.value });
						}}
						placeholder="Enter product name"
						enterButton={
							<Button
								type="primary"
								htmlType="button"
								icon={<SearchOutlined />}
								loading={
									this.props.isLoading || this.state.loading
								}
								disabled={isSearchDisabled}
							>
								Search
							</Button>
						}
						onSearch={currentKeywords => {
							this.setState({ searchQuery: currentKeywords });
							this.handleSearch(currentKeywords, 1);
						}}
						disabled={isSearchDisabled}
					/>
				</Col>
				{this.state.currentItemSource === ProductSource.Icecat && (
					<Col span={24} className="mt-3">
						<UnifiedItemFiltersMenu
							metaInfo={this.state.metaInfo}
							selectedManufacturers={
								this.state.filters.manufacturers
							}
							selectedCategories={this.state.filters.categories}
							selectedIcecatCategories={
								this.state.filters.icecat_category_ids
							}
							selectedProductTypes={this.state.filters.item_types}
							selectedSuppliers={this.state.filters.supplier_ids}
							selectedSyncSources={
								this.state.filters.sync_sources
							}
							excludeOutOfStock={
								this.state.filters.exclude_out_of_stock
							}
							filterByConnectedDistributors={
								this.state.filters
									.filter_by_connected_distributors
							}
							excludeDropShipWH={
								this.state.filters.exclude_drop_ship_wh
							}
							disableLimitDistributorOption={
								!this.state.dataSource.length &&
								!this.state.filters
									.filter_by_connected_distributors &&
								!!this.props.isDickerDataIntegrated
							}
							sortBy={this.state.sortBy}
							onApply={this.handleFiltersApply}
							onSortChange={this.handleSortingOptionChange}
						/>
					</Col>
				)}
				{this.state.currentItemSource === ProductSource.Amazon &&
					this.props.isAmazonDistributorIntegrated && (
						<Col span={24} className="mt-3">
							<AmazonItemFiltersMenu
								metaInfo={this.state.amazonMetaInfo}
								amazonFilterState={this.state.amazonFilterState}
								amazonAppliedFilters={
									this.props.amazonAppliedFilters
								}
								onApply={this.onApplyAmazonFilters}
								onFilterChange={this.handleAmazonFilterChange}
								onClearFilters={this.handleClearAmazonFilters}
								onFreeShippingChange={
									this.handleAmazonFreeShippingChange
								}
								onPrimeEligibleChange={
									this.handleAmazonPrimeEligibleChange
								}
								isVisible={this.state.amazonFilterMenuVisible}
								onVisibleChange={
									this.handleAmazonFilterVisibilityChange
								}
								loading={false}
							/>
						</Col>
					)}
			</Row>
		);
	};

	render() {
		const isAmazonNotIntegrated =
			this.state.currentItemSource === ProductSource.Amazon &&
			!this.props.isAmazonDistributorIntegrated;

		if (isAmazonNotIntegrated) {
			return (
				<>
					<Row className="amazon-integrate-form-header">
						{this.renderTableHeader()}
					</Row>
					<IntegrateAmazonForm />
					<div className="ant-drawer-footer">
						<ZButton
							type="default"
							htmlType="button"
							onClick={this.props.onClose}
							disabled={
								this.props.isLoading || this.state.loading
							}
							className="mr-4"
						>
							Cancel
						</ZButton>
						<ZButton
							type="primary"
							htmlType="button"
							disabled={true}
						>
							Add Products
						</ZButton>
					</div>
				</>
			);
		}

		const { selectedProductList } = this.state;

		const productRowSelection: TableRowSelection<Product> = {
			onChange: (
				selectedProductIdList: React.ReactText[],
				selectedProducts: Product[]
			) => {
				const updatedSelectedProducts = selectedProductIdList
					.map(productId => {
						const currentProductData = this.state.dataSource.find(
							product => product.id === productId
						);
						if (currentProductData) {
							return currentProductData;
						}
						const previousProductData =
							this.state.selectedProductList.find(
								product => product.id === productId
							);
						if (previousProductData) {
							return previousProductData;
						}
						return selectedProducts.find(
							product => product.id === productId
						);
					})
					.filter(product => product) as Product[];

				this.setState({
					selectedProductList: updatedSelectedProducts
				});
			},
			type:
				this.props.itemType === ProductType.Bundle
					? "radio"
					: "checkbox",
			selectedRowKeys: selectedProductList.map(product => product?.id),
			columnWidth: 20,
			preserveSelectedRowKeys: true,
			getCheckboxProps: (record: Product) => {
				const isMarketplaceSku =
					record.product_source === ProductSource.ZomentumMarketplace;
				const currentProductVendorStatus =
					this.props.vendorProgramDetails?.find(v => {
						return v.id === record.vendor_company_id;
					})?.status ?? VendorSubscriptionStatus.NOTAPPLIED;
				return {
					disabled:
						this.state.pricingLoading ||
						this.state.loading ||
						(isMarketplaceSku &&
							currentProductVendorStatus !==
								VendorSubscriptionStatus.ACCEPTED)
				};
			}
		};

		return (
			<div ref={this.tableContainerRef}>
				<Row className="drawer-form-container no-padding">
					<Col
						span={24}
						className="table-page-container product-table-container"
					>
						<ConfigProvider
							renderEmpty={
								!this.state.loading
									? this.customEmptyProductsTable
									: undefined
							}
						>
							<ZTable
								title={this.renderTableHeader}
								showHeader={false}
								loading={
									this.props.isLoading ||
									(this.state.loading &&
										this.state.currentPageNo === 1 &&
										!this.state.pricingLoading)
								}
								scroll={{ scrollToFirstRowOnChange: false }}
								size="small"
								dataSource={this.state.dataSource}
								columns={this.productColumns}
								rowKey="id"
								pagination={false}
								rowSelection={productRowSelection}
								onRow={this.handleProductRowEvent}
								className="product-table"
								footer={
									this.state.loading &&
									this.state.dataSource.length > 0
										? () => (
												<div className="text-center py-4">
													<ZText>
														Loading more products...
													</ZText>
												</div>
										  )
										: undefined
								}
							/>
						</ConfigProvider>
					</Col>
				</Row>
				<div
					className="ant-drawer-footer"
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center"
					}}
				>
					<Checkbox
						checked={this.state.shouldGroupByMPN}
						onChange={this.handleGroupByMPNChange}
					>
						Group Products by Manufacturer Part Number (MPN)
					</Checkbox>
					<div>
						<Button
							type="default"
							htmlType="button"
							onClick={this.props.onClose}
							disabled={
								this.props.isLoading || this.state.loading
							}
							className="mr-4"
						>
							Cancel
						</Button>
						<Button
							type="primary"
							htmlType="button"
							loading={
								this.props.isLoading ||
								this.state.loading ||
								!this.state.dataSource
							}
							disabled={
								!this.state.selectedProductList.length ||
								this.state.pricingLoading
							}
							onClick={this.onSubmit}
						>
							{this.props.itemType === ProductType.Bundle
								? "Next"
								: "Add Products"}
						</Button>
					</div>
				</div>

				<AddProductDrawerV2
					visible={this.state.createProductButtonVisible}
					onClose={this.handleOnClose}
					onAdd={this.handleCreateProductAdded}
					drawerState={EProductDrawerState.ADD}
				/>

				{this.props.itemType === ProductType.Bundle ? (
					this.props.configureDrawer
				) : (
					<ZUpdateMandatoryFieldsForQuoteBlockDrawer
						visible={
							(this.props.unfilledSelectedQuoteProductIds ?? [])
								.length > 0
						}
						onClose={() => this.props.resetQuoteProducts?.()}
						renderProductColumn={this.renderProductColumn(true)}
						products={this.state.selectedProductList}
						unfilledProducts={this.state.selectedProductList.filter(
							product =>
								(
									this.props
										.unfilledSelectedQuoteProductIds ?? []
								).some(id => id === product.id)
						)}
						removeProducts={(products: any) =>
							this.removeItemsFromUpdateMandatoryFieldsDrawer(
								products
							)
						}
					>
						{this.props.configureDrawer}
					</ZUpdateMandatoryFieldsForQuoteBlockDrawer>
				)}
			</div>
		);
	}
}

const UnifiedSearchDocumentProductCatalogDrawer: React.FC<Props> = props => {
	const dispatch = useDispatch();
	const marketplaceFlag = useFeatureFlag("marketplace_sku");
	const showPartnerAlignFeaturesFlag = useFeatureFlag(
		"show_partner_align_features"
	);
	const alwaysAllowAmazonDistributor = useFeatureFlag(
		"always_allow_amazon_distributor"
	);
	const newPricingConstraints = useFeatureFlag("new_pricing_constraints");

	const hasGrowthPlanAccess = useFeatureAccess(
		PricingPlanNames.GROWTH,
		UserRole.UserRole
	);
	const hasExpandPlanAccess = useFeatureAccess(
		PricingPlanNames.EXPAND,
		UserRole.UserRole
	);
	const createProductPermission = usePermissions(
		Modules.ITEM,
		ModulePermission.CREATE_EDIT_DELETE_OWNED
	);

	const {
		isInternalUser,
		vendorProgramDetails,
		isAmazonDistributorIntegrated,
		amazonFilterState,
		amazonAppliedFilters,
		hasAmazonAddOn,
		isDickerDataIntegrated
	} = useSelector((state: GlobalState) => ({
		isInternalUser: state.user.billingInfo.is_internal,
		vendorProgramDetails: state.marketplace.vendorProgamDetails,
		isAmazonDistributorIntegrated:
			!!state.user.userCompany.integrations.amazon_business,
		amazonFilterState: (state.user as any).amazonFilterState,
		amazonAppliedFilters: state.user.amazonAppliedFilters || undefined,
		hasAmazonAddOn: !!state.user.subscriptionDetails.addons.find(addOn =>
			addOn.id?.includes(PricingAddOnEnum.AMAZON_BUSINESS)
		),
		isDickerDataIntegrated:
			!!state.user.userCompany.integrations.dicker_data
	}));

	const hasExpandPlanWithAmazonAddOn = hasExpandPlanAccess && hasAmazonAddOn;
	const hasPermissionForAmazonDistributor = newPricingConstraints
		? alwaysAllowAmazonDistributor ||
		  hasExpandPlanWithAmazonAddOn ||
		  hasGrowthPlanAccess ||
		  isInternalUser
		: alwaysAllowAmazonDistributor || hasGrowthPlanAccess || isInternalUser;

	const updateSelectedProduct = (selectedProduct: Product) => {
		proposalBuilderStore.dispatch(
			ProductsActionCreators.setSelectedEtilizeProduct(selectedProduct)
		);
	};

	const updateAmazonAppliedFilters = (
		amazonAppliedFilters: AmazonProductAppliedFilters
	) => {
		dispatch(
			UsersActionCreators.updateAmazonProductAppliedFilter(
				amazonAppliedFilters
			)
		);
	};

	return (
		<UnifiedSearchDocumentProductCatalogDrawerClass
			{...props}
			marketplaceFlag={marketplaceFlag}
			showPartnerAlignFeaturesFlag={showPartnerAlignFeaturesFlag}
			isInternalUser={isInternalUser}
			isAmazonDistributorIntegrated={isAmazonDistributorIntegrated}
			vendorProgramDetails={vendorProgramDetails}
			updateSelectedProduct={updateSelectedProduct}
			hasPermissionForAmazonDistributor={
				hasPermissionForAmazonDistributor
			}
			createProductPermission={createProductPermission}
			amazonFilterState={amazonFilterState}
			amazonAppliedFilters={amazonAppliedFilters}
			updateAmazonAppliedFilters={updateAmazonAppliedFilters}
			isDickerDataIntegrated={isDickerDataIntegrated}
		/>
	);
};

export default UnifiedSearchDocumentProductCatalogDrawer;
